/**
 * Copyright (c) 2024 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Florian Hartl <f.hartl@techdivision.com>
 *
 */

import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box, Grid, Typography } from '@mui/material';
import { ContractServices } from './ContractServices';
import { ContractFootnotes } from './ContractFootnotes';
import theme from '../../../../../Config/ThemeConfig';
import { useContractSelection } from './useContractSelection';
import { ContractPricing } from './ContractPricing';
import { ContractVolumeInformation } from './ContractVolumeInformation';

/**
 * ContactForm Component
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
export function ContractSelection(props) {
  const {
    contract,
    setAsSelectedContract,
    selectedTerm,
    isSelected,
  } = props;
  const { t } = useTranslation('translations');
  const {
    getValuesByPropName,
    getContractOrTermValue,
    changeContractSelection,
  } = useContractSelection({
    contract,
    selectedTerm,
    setAsSelectedContract,
  });
  const priceData = getValuesByPropName('price');
  const priceAdjustmentRules = getValuesByPropName('priceAdjustmentRules');
  const contractVolumeInformation = getValuesByPropName('contractVolumeInformation');
  const columnStyles = {
    borderBottom: `1px solid ${theme.palette.border.default}`,
    padding: '0.5rem',
  };

  /**
   * @param showContractName
   * @param showTotalContractVolume
   * @returns {React.JSX.Element}
   */
  const renderContractBaseInfo = (showContractName = true, showTotalContractVolume = true, showContractDescription = true) => (
    <Box
      sx={{
        textAlign: 'center',
        padding: '0.5rem',
        minHeight: showTotalContractVolume ? '170px' : '130px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        ...columnStyles,
      }}
    >
      {showContractName ? (
        <Typography mb={1}>
          {getContractOrTermValue('name', true)}
        </Typography>
      )
        : null}
      <Typography
        mb={1}
        sx={{
          color: theme.palette.primary.main,
          fontWeight: 'bold',
        }}
      >
        {selectedTerm}
        {' '}
        {selectedTerm === 1 ? t('months')
          .slice(0, -1) : t('months')}
      </Typography>
      <ContractPricing
        priceData={priceData}
        priceAdjustmentRules={priceAdjustmentRules}
      />
      {Array.isArray(priceAdjustmentRules.newValue) ? (
        <Box mt={2} sx={{ fontSize: '12px' }}>
          *
          {' '}
          {priceAdjustmentRules.newValue[0]?.defaultDescription}
        </Box>
      ) : null}
      {showContractDescription
        ? (
          <Typography sx={{ fontSize: '12px' }} mb={1} mt={1}>
            {getContractOrTermValue('description', true)}
          </Typography>
        )
        : null}
      {showTotalContractVolume ? (
        <ContractVolumeInformation
          contractVolumeInformation={contractVolumeInformation?.value}
          newContractVolumeInformation={contractVolumeInformation?.newValue}
          showTotalContractVolume={showTotalContractVolume}
        />
      )
        : null}
    </Box>
  );

  /**
   * @returns {JSX.Element}
   */
  const renderMoreInformation = () => {
    const fontSize = { fontSize: '0.85rem' };
    const flatFees = getContractOrTermValue('flatFees');
    const contractDescription = getContractOrTermValue('description', true);
    return (
      <>
        {contractDescription ? <Typography sx={fontSize} mb={2}>{contractDescription}</Typography> : null}
        {flatFees && flatFees.length ? (
          <Typography
            mb={1}
            sx={{
              ...fontSize,
              fontWeight: 'bold',
            }}
          >
            {t('flatFees')}
          </Typography>
        ) : null}
        {flatFees.map((fee) => (
          <Box mb={1} key={fee.identifier}>
            <Typography sx={{
              ...fontSize,
              textDecoration: 'underline',
            }}
            >
              {fee.name}
              :
            </Typography>
            <Typography sx={fontSize}>
              {fee.price}
              {' '}
              €
              {fee.paymentFrequency ? ` (${fee.paymentFrequency})` : null}
            </Typography>
          </Box>
        ))}
      </>
    );
  };

  return (
    <Grid
      item
      xs={12}
      sm={6}
      lg={3}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.palette.background.gridTable,
        border: `3px solid ${isSelected ? theme.palette.border.active : theme.palette.border.default}`,
        cursor: 'pointer',
        maxWidth: {
          xs: '100%',
          sm: 'calc(50% - 1rem)',
          lg: '25%',
        },
        marginBottom: {
          xs: '2rem',
          sm: '2rem',
          lg: '0',
        },
        '&:last-child': {
          marginBottom: {
            xs: '0',
            sm: '2rem',
            lg: '0',
          },
        },
        '&:nth-of-type(odd)': {
          marginRight: {
            xs: '0',
            sm: '1rem',
            lg: '0',
          },
        },
        '&:nth-of-type(even)': {
          marginLeft: {
            xs: '0',
            sm: '1rem',
            lg: '0',
          },
        },
      }}
      onClick={changeContractSelection}
      data-stop-blocking-event
    >
      <Box>
        <img
          alt={getContractOrTermValue('name', true)}
          style={{
            width: '100%',
            objectFit: 'cover',
          }}
          data-cmp-ab="2" // Exclude the image from auto blocking
          src={getContractOrTermValue('imageUrl', true)}
        />
        {renderContractBaseInfo(true, false, true)}
      </Box>
      <Box
        sx={{
          borderTop: 'none',
          ...columnStyles,
        }}
        flexGrow={1}
      >
        <ContractServices limitation={10} services={getContractOrTermValue('modules', true)} />
      </Box>
      <Box>
        <ContractFootnotes
          sx={{
            ...columnStyles,
            py: 0,
          }}
          text={renderMoreInformation()}
        />
        {renderContractBaseInfo(false, true, false)}
      </Box>
    </Grid>
  );
}

ContractSelection.propTypes = {
  contract: PropTypes.object.isRequired,
  setAsSelectedContract: PropTypes.func.isRequired,
  selectedTerm: PropTypes.number.isRequired,
  isSelected: PropTypes.bool.isRequired,
};
